import React from 'react';
import { motion } from 'framer-motion';
import { 
  Truck, 
  TrendingUp, 
  MapPin, 
  Clock,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  LineChart,
  Info,
  Package
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

interface CTEModalProps {
  data: {
    total_ctes: number;
    cte_analytics: {
      status_distribution: {
        completed: number;
        in_transit: number;
        pending: number;
        cancelled: number;
      };
      monthly_volume: Array<{
        month: string;
        volume: number;
        efficiency: number;
      }>;
      top_routes: Array<{
        route: string;
        volume: number;
        efficiency: number;
        avg_time: number;
      }>;
      cte_by_type: Array<{
        type: string;
        count: number;
        percentage: number;
      }>;
    };
  };
}

export const CTEModal: React.FC<CTEModalProps> = ({ data }) => {
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280'];

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Entregues
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.cte_analytics.status_distribution.completed)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              CTEs finalizados
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Em Trânsito
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.cte_analytics.status_distribution.in_transit)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              CTEs em movimento
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Cancelados
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.cte_analytics.status_distribution.cancelled)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              CTEs cancelados
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 shadow-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Pendentes
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.cte_analytics.status_distribution.pending)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              CTEs aguardando
            </p>
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Volume Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <LineChart 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Volume Mensal de CTEs
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.cte_analytics.monthly_volume}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [formatNumber(value), 'Volume']}
              />
              <Area 
                type="monotone" 
                dataKey="volume" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* CTE by Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <BarChart3 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            CTEs por Tipo de Carga
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.cte_analytics.cte_by_type}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="type" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [formatNumber(value), 'Quantidade']}
              />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Top Routes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <MapPin 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Principais Rotas
        </h3>
        <div className="space-y-4">
          {data.cte_analytics.top_routes.map((route, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                opacity: 0.6
              }}
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    opacity: 0.6
                  }}
                >
                  <span 
                    className="text-sm font-bold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {index + 1}
                  </span>
                </div>
                <div>
                  <h4 
                    className="font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {route.route}
                  </h4>
                  <p 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Tempo médio: {route.avg_time}h
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div 
                  className="text-lg font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(route.volume)}
                </div>
                <div 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {route.efficiency}% eficiência
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Data Source Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="p-4 rounded-lg border relative overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div className="relative">
          <div className="flex items-start space-x-3">
          <Info 
            className="h-5 w-5 mt-0.5 flex-shrink-0" 
            style={{ color: 'var(--primary)' }}
          />
          <div>
            <h4 
              className="font-semibold mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Origem dos Dados
            </h4>
            <p 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Os dados de CTEs são coletados automaticamente do sistema de gestão de transporte, 
              incluindo informações sobre status, rotas, tipos de carga e tempos de entrega. 
              Atualizados em tempo real conforme o progresso das entregas.
            </p>
          </div>
        </div>
        </div>
      </motion.div>
    </div>
  );
};
